import isEqual from "lodash/isEqual";
import React, { KeyboardEvent, useEffect, useState } from "react";

import Box from "veneer/Box";
import Stack from "veneer/Stack";

import type { Media } from "cortex/ui/src/fleet/view/media-library/model";
import { ContentArchived } from "cortex/ui/src/ui/content-archived";

import { ContentGroupNavigator } from "./ContentGroupNavigator";
import { ContentGroupPreview } from "./ContentGroupPreview";
import { ContentPreview } from "./ContentPreview";
import { EditContentForm } from "./EditContentForm";
import { EditContentGroupForm } from "./EditContentGroupForm";
import { type FormConfig, type GetChildState } from "./sidebarTypes";

interface ContentGroupEditorProps {
  categories?: ReadonlyArray<string>;
  getChildState?: GetChildState;
  getConfig: (type?: string | null, mimeType?: string | null) => FormConfig;
  hideFullscreen?: () => void;
  isStrategy?: boolean;
  mediaEntry?: Partial<Media>;
  showFullscreen?: (
    url: string,
    mediaType: "image" | "video" | null,
    filename?: string,
  ) => void;
}

export function ContentGroupEditor({
  categories = [],
  getChildState = () => {},
  getConfig,
  hideFullscreen = () => {},
  isStrategy,
  mediaEntry = {},
  showFullscreen = () => {},
}: ContentGroupEditorProps) {
  const [index, setIndex] = useState(0);
  const [previousMediaEntry, setPreviousMediaEntry] = useState(mediaEntry);

  useEffect(() => {
    if (!isEqual(mediaEntry, previousMediaEntry)) {
      setIndex(0);
      setPreviousMediaEntry(mediaEntry);
    }
  }, [mediaEntry, previousMediaEntry]);

  const { content = [], mimeType, numberOfSpots, type } = mediaEntry;
  const activeMediaEntry = (content?.[index] ?? {}) as Partial<Media>;
  const {
    mimeType: activeMimeType,
    name: activeName,
    type: activeType,
    url: activeUrl,
  } = activeMediaEntry;

  const activeConfig = getConfig(activeType, activeMimeType);
  const config = getConfig(type, mimeType);

  const navigateTo = (nextIndex: number) => {
    setIndex(nextIndex);
  };

  const navigate = (direction: number) => {
    const total = numberOfSpots ?? content?.length ?? 0;

    if (direction > 0 && index === total - 1) {
      return;
    }

    if (direction < 0 && index === 0) {
      return;
    }

    navigateTo(index + direction);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft") {
      navigate(-1);
    }

    if (event.key === "ArrowRight") {
      navigate(1);
    }
  };

  return (
    <Box
      dataTestId="content-group-editor"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <ContentGroupNavigator
        index={index}
        onNavigate={navigate}
        totalElements={numberOfSpots ?? content?.length ?? 0}
      />
      <ContentPreview
        hideFullscreen={hideFullscreen}
        mimeType={activeMimeType ?? undefined}
        name={activeName ?? undefined}
        showFullscreen={showFullscreen}
        type={activeType ?? undefined}
        url={activeUrl ?? undefined}
      />
      <ContentGroupPreview
        index={index}
        mediaEntries={content as ReadonlyArray<Partial<Media>>}
        navigateTo={navigateTo}
      />
      <ContentArchived mediaEntry={mediaEntry} parentOnly={true} />
      <ContentArchived mediaEntry={activeMediaEntry} />
      <Stack gap={2}>
        <EditContentGroupForm
          getState={getChildState}
          mediaEntry={mediaEntry}
          readOnlyFields={config.readOnlyFields as ReadonlyArray<"name">}
        />
        <EditContentForm
          categories={categories}
          excludeFields={activeConfig.excludeFields}
          getState={getChildState}
          mediaEntry={activeMediaEntry}
          readOnlyFields={isStrategy ? activeConfig.readOnlyFields : ["all"]}
        />
      </Stack>
    </Box>
  );
}
